import { useSyncExternalStore } from 'react';

/**
 * Singleton store for the global "Payment failed" popup.
 *
 * State:
 *   isOpen   — whether the popup is currently rendered.
 *   message  — body text shown below the title.
 *   buttons  — array of { text, onClick?, variant? } describing action buttons.
 *
 * Buttons:
 *   - text     (string, required)        — label rendered on the button.
 *   - onClick  (function, optional)      — invoked on press. The popup auto-closes
 *                                          after onClick returns; call
 *                                          showPaymentErrorPopup() again from inside
 *                                          onClick if you need to keep something open.
 *   - variant  ('primary' | 'secondary') — visual style. Defaults to 'primary' for
 *                                          the first button and 'secondary' for the
 *                                          rest when omitted.
 */

let state = {
    isOpen: false,
    message: '',
    buttons: [],
};

const listeners = new Set();

function emit() {
    for (const l of listeners) l();
}

export const paymentErrorPopupStore = {
    getState() { return state; },
    setState(patch) {
        state = { ...state, ...patch };
        emit();
    },
    subscribe(listener) {
        listeners.add(listener);
        return () => listeners.delete(listener);
    },
};

export function usePaymentErrorPopup() {
    return useSyncExternalStore(
        paymentErrorPopupStore.subscribe,
        paymentErrorPopupStore.getState,
        paymentErrorPopupStore.getState
    );
}

/**
 * Show the global "Payment failed" popup.
 *
 * @param {Object}   opts
 * @param {string}   opts.message  Body text shown under the "Payment failed" title.
 * @param {Array}    [opts.buttons=[{ text: 'OK' }]]
 *                   Action buttons. Each item: { text, onClick?, variant? }.
 *                   See store header comment for details.
 */
export function showPaymentErrorPopup({ message = '', buttons } = {}) {
    const safeButtons = Array.isArray(buttons) && buttons.length > 0
        ? buttons
        : [{ text: 'OK' }];

    paymentErrorPopupStore.setState({
        isOpen: true,
        message: String(message ?? ''),
        buttons: safeButtons,
    });
}

export function hidePaymentErrorPopup() {
    paymentErrorPopupStore.setState({
        isOpen: false,
        message: '',
        buttons: [],
    });
}
